import { interpolateString } from "@memorang/helpers/common";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useDevice } from "../../../../useDevice";
import AudioPlayerContainer from "../../../AudioPlayerContainer";
import Box from "../../../Box";
import InfoAlert from "../../../InfoAlert";
import ScrollContainer from "../../../ScrollContainer";
import { getPostAnswerContentPadding } from "../../constants";
import type { ItemAudioGate } from "../../types";
import PassageBanner from "./PassageBanner";
import StimulusLayout from "./StimulusLayout";
import ToeicSlot, { type ToeicItemData } from "./ToeicSlot";
import type { LayoutConfig, Slot } from "./toeic-layout-config";
import { useMobilePassageSheet } from "./useMobilePassageSheet";

type StackedStimulusHeaderProps = {
	title: string;
	skill: ToeicItemData["skill"];
	trailingCaption: string;
	audioUri?: string;
	audioGate?: ItemAudioGate;
	instruction?: string;
};

const StackedStimulusHeader = ({
	title,
	skill,
	trailingCaption,
	audioUri,
	audioGate,
	instruction,
}: StackedStimulusHeaderProps) => (
	<>
		<PassageBanner
			title={title}
			skill={skill}
			trailingCaption={trailingCaption}
		/>
		{audioUri ? (
			<Box marginHorizontal={-16}>
				<AudioPlayerContainer
					ref={audioGate?.playerRef}
					uri={audioUri}
					waveformWidth={200}
					showWaveform
					useReanimated={false}
					playOnce={audioGate?.playOnce}
					forceCompleted={audioGate?.forceCompleted}
					onEnd={audioGate?.onEnd}
					borderRadius={0}
				/>
			</Box>
		) : null}
		{instruction ? (
			<InfoAlert
				text={instruction}
				marginHorizontal={-16}
				borderRadius={0}
				paddingHorizontal={16}
				paddingVertical={8}
			/>
		) : null}
	</>
);

const resolveStackedStimulusInstruction = (data: ToeicItemData) =>
	data.currentStimulusItemIndex === 1 &&
	data.instruction &&
	data.numTotalStimulusItems != null
		? interpolateString(data.instruction, { n: data.numTotalStimulusItems })
		: undefined;

export type ToeicItemRendererProps = {
	data: ToeicItemData;
	itemId: number;
	layoutConfig: LayoutConfig;
	isSplitLayout: boolean;
	isShellSplitLayout?: boolean;
	isSummativeTest?: boolean;
	renderFeedback?: () => ReactNode;
	renderHint?: () => ReactNode;
	renderTTS?: () => ReactNode;
	audioGate?: ItemAudioGate;
	autoOpenPassage?: boolean;
};

const ToeicItemRenderer = ({
	data,
	itemId,
	layoutConfig,
	isSplitLayout,
	isShellSplitLayout,
	isSummativeTest,
	renderFeedback,
	renderHint,
	renderTTS,
	audioGate,
	autoOpenPassage,
}: ToeicItemRendererProps) => {
	const showTTS = data.skill === "LISTENING";
	const { t, i18n } = useTranslation();
	const { isMobile, isMediumWindowSize } = useDevice();

	const skillKey = `session.stimulusTypes.${data.skill.toLowerCase()}`;
	const formattedSkill = i18n.exists(skillKey) ? t(skillKey) : data.skill;
	const sectionHeaderLabel = `${formattedSkill} ${t("session.attributes.stimulus")}`;

	const bannerTitle = data.passageTitle || sectionHeaderLabel;

	const hasStimulusItemCount = Boolean(
		data.currentStimulusItemIndex && data.numTotalStimulusItems,
	);

	const { renderMobilePassageBanner } = useMobilePassageSheet({
		title: bannerTitle,
		openCta:
			data.skill === "LISTENING"
				? t("session.passageStem.listen")
				: t("session.passageStem.openPassage"),
		skill: data.skill,
		stimulusCaption: hasStimulusItemCount
			? t("session.passageStem.itemOfTotal", {
					current: data.currentStimulusItemIndex,
					total: data.numTotalStimulusItems,
				})
			: undefined,
	});

	const renderSlots = (
		slots: Slot[],
		isLeftPanel = false,
		options?: {
			slotData?: ToeicItemData;
			stimulusFullBleed?: boolean;
			hideStimulusAudio?: boolean;
		},
	) =>
		slots.map((slot) => (
			<ToeicSlot
				key={slot}
				slot={slot}
				data={options?.slotData ?? data}
				itemId={itemId}
				isSummativeTest={isSummativeTest}
				renderFeedback={renderFeedback}
				renderHint={renderHint}
				showTTS={showTTS}
				renderTTS={renderTTS}
				audioGate={audioGate}
				renderInstructionAsAlert={isLeftPanel}
				stimulusFullBleed={options?.stimulusFullBleed}
				hideStimulusAudio={options?.hideStimulusAudio}
			/>
		));

	const hasStimulusPanel = isSplitLayout && layoutConfig.left.length > 0;

	const answeredBottomPadding = getPostAnswerContentPadding();

	if (hasStimulusPanel) {
		return (
			<StimulusLayout
				skill={data.skill}
				isAnswered={data.isAnswered}
				autoOpenPassage={autoOpenPassage}
				renderLeftContent={() => (
					<Box gap={12}>{renderSlots(layoutConfig.left, true)}</Box>
				)}
				renderBottomSheetContent={() => (
					<Box gap={12}>{renderSlots(layoutConfig.left, true)}</Box>
				)}
				renderMobilePassageBanner={renderMobilePassageBanner}
				renderRight={() => (
					<Box gap={12}>{renderSlots(layoutConfig.right)}</Box>
				)}
			/>
		);
	}

	const showStackedStimulusHeader = isMobile && hasStimulusItemCount;
	const stackedStimulusInstruction = showStackedStimulusHeader
		? resolveStackedStimulusInstruction(data)
		: undefined;

	return (
		<Box flex={1}>
			{showStackedStimulusHeader ? (
				<StackedStimulusHeader
					title={bannerTitle}
					skill={data.skill}
					trailingCaption={t("session.passageStem.questionOfTotal", {
						current: data.currentStimulusItemIndex,
						total: data.numTotalStimulusItems,
					})}
					audioUri={data.leftAudioUri}
					audioGate={audioGate}
					instruction={stackedStimulusInstruction}
				/>
			) : null}
			<ScrollContainer
				stableScrollbarGutter
				contentContainerStyle={{
					paddingBottom:
						isMobile && data.isAnswered ? answeredBottomPadding : 120,
					...(isShellSplitLayout && !isMobile && isMediumWindowSize
						? { paddingTop: 24, paddingHorizontal: 24 }
						: {}),
				}}
			>
				<Box gap={12}>
					{renderSlots(
						layoutConfig.stacked,
						false,
						showStackedStimulusHeader
							? {
									slotData: {
										...data,
										passageTitle: undefined,
										instruction: "",
									},
									stimulusFullBleed: true,
									hideStimulusAudio: Boolean(data.leftAudioUri),
								}
							: undefined,
					)}
				</Box>
			</ScrollContainer>
		</Box>
	);
};

export default ToeicItemRenderer;
